import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import type { Account, AccountSubscriptionStatus } from '../api/client';
import { useAccounts } from '../context/AccountsContext';
import { useAuth } from '../context/AuthContext';
import { DashboardBoard } from '../dashboard/DashboardBoard';
import { hasPaidPlan, isActiveOnDemand, isOnDemand, isPlanCovered } from '../utils/subscription';
import { AccountAvatar } from '../components/AccountAvatar';
import { Button } from '@heroui/react';
import { FeatherIcon } from '../components/FeatherIcon';
import { DashboardSkeleton } from '../components/LoadingSkeletons';
import { PageTransition } from '../components/PageTransition';
import { PageHeading } from '../components/PageHeader';
import { toastQueue } from '../components/ToastProvider';

type FilterTab = 'all' | 'active' | 'inactive';

/** enabled AND logged in — the only state where the account is really working. */
const isActive = (a: Account) => Boolean(a.is_enabled && a.is_logged_in);
const accountName = (a: Account) => a.profile_name || a.username || a.account_id;

// Icon-chip tones mapped onto the shared app tokens (see index.css).
const TONES = {
  neutral: { icon: 'text-[var(--color-text-secondary)]', bg: 'bg-[var(--color-bg-subtle)]', val: 'text-[var(--color-text-primary)]' },
  ok: { icon: 'text-[var(--stat-enabled)]', bg: 'bg-[var(--stat-enabled-bg)]', val: 'text-[var(--color-text-primary)]' },
  accent: { icon: 'text-[var(--accent-tertiary)]', bg: 'bg-[var(--stat-models-bg)]', val: 'text-[var(--color-text-primary)]' },
  warn: { icon: 'text-[#f5a524]', bg: 'bg-[#f5a524]/15', val: 'text-[#f5a524]' },
  bad: { icon: 'text-[var(--error)]', bg: 'bg-[var(--error-bg)]', val: 'text-[var(--error)]' },
} as const;
type Tone = keyof typeof TONES;

/** Per-account on/off switch — mirrors the bulk "Turn all on/off" controls. */
function PowerToggle({ on, disabled, onChange }: { on: boolean; disabled?: boolean; onChange: (next: boolean) => void }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      disabled={disabled}
      onClick={(e) => { e.stopPropagation(); onChange(!on); }}
      className="relative shrink-0 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      style={{ width: 40, height: 23, background: on ? 'var(--stat-enabled)' : 'color-mix(in srgb, var(--om-ink) 12%, transparent)' }}
    >
      <span
        className="absolute rounded-full bg-white transition-all"
        style={{ top: 3, left: on ? 20 : 3, width: 17, height: 17, boxShadow: '0 1px 3px rgba(0,0,0,.4)' }}
      />
    </button>
  );
}

function StatTile({ icon, tone, value, label, numeric }: { icon: string; tone: Tone; value: string | number; label: string; numeric?: boolean }) {
  const c = TONES[tone];
  return (
    <div className="rounded-2xl bg-[var(--color-bg-card)] border border-[var(--color-border-default)] px-4 py-3.5 flex items-center gap-3">
      <span className={`w-9 h-9 rounded-xl shrink-0 flex items-center justify-center ${c.bg} ${c.icon}`}>
        <FeatherIcon name={icon} size={18} />
      </span>
      <div className="min-w-0">
        <div
          className={`${numeric ? 'text-2xl font-bold tabular-nums' : 'text-[15px] font-semibold'} leading-tight truncate ${c.val}`}
          title={typeof value === 'string' ? value : undefined}
        >
          {value}
        </div>
        <div className="text-xs text-[var(--color-text-tertiary)] mt-1">{label}</div>
      </div>
    </div>
  );
}

/**
 * ADR 0030: the customisable Dashboard ships to super admins first; everyone else
 * keeps the classic page until this flips for all (then the classic page goes).
 */
const NEW_DASHBOARD_FOR_EVERYONE = false;

export function DashboardPage() {
  const { isSuperAdmin } = useAuth();
  const [params] = useSearchParams();
  const offered = NEW_DASHBOARD_FOR_EVERYONE || isSuperAdmin;
  if (offered && params.get('classic') !== '1') return <DashboardBoard />;
  return <ClassicDashboard offerNew={offered} />;
}

/** The older Dashboard: plan tiles + Account cards with AI switches. */
function ClassicDashboard({ offerNew }: { offerNew: boolean }) {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  // The shell's Accounts list: shown at once on every visit, kept fresh in the background.
  const { accounts, loaded, subscriptionMap, subscriptionsState, updateAccount } = useAccounts();
  // Accounts throttled (rate-limited into silence) right now — fleet marker + tile.
  const [throttledIds, setThrottledIds] = useState<Set<string>>(new Set());
  const loading = !loaded || subscriptionsState === 'loading';
  const [bulkToggling, setBulkToggling] = useState<null | 'on' | 'off'>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [tab, setTab] = useState<FilterTab>('all');
  const [query, setQuery] = useState('');

  const hasSelection = selectionMode && selectedIds.size > 0;

  const toggleSelected = (accountId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
  };

  const exitSelection = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  const handleBulkToggle = async (enabled: boolean) => {
    if (bulkToggling) return;
    // In selection mode act on the chosen accounts; otherwise act on all.
    const scope = hasSelection
      ? accounts.filter((a) => selectedIds.has(a.account_id))
      : accounts;
    // Only act on accounts that actually need changing.
    const targets = scope.filter((a) => a.is_enabled !== enabled);
    if (targets.length === 0) {
      toastQueue.add({
        title: enabled ? t('dashboard.bulkAllOnAlready') : t('dashboard.bulkAllOffAlready'),
      });
      return;
    }

    setBulkToggling(enabled ? 'on' : 'off');
    const results = await Promise.allSettled(
      targets.map((a) => api.toggleAccount(a.account_id, enabled)),
    );

    const updatedById = new Map<string, Account>();
    let failed = 0;
    results.forEach((res, i) => {
      const target = targets[i];
      if (res.status === 'fulfilled' && target) {
        updatedById.set(target.account_id, res.value);
      } else {
        failed += 1;
      }
    });

    updatedById.forEach((updated, id) => updateAccount(id, updated));

    const succeeded = updatedById.size;
    if (failed === 0) {
      toastQueue.add({
        title: enabled
          ? t('dashboard.bulkTurnedOn', { count: succeeded })
          : t('dashboard.bulkTurnedOff', { count: succeeded }),
      });
    } else {
      toastQueue.add({
        title: t('dashboard.bulkPartial', { succeeded, failed }),
      });
    }

    setBulkToggling(null);
  };

  const handleToggleOne = async (account: Account, enabled: boolean) => {
    if (togglingId || bulkToggling) return;
    setTogglingId(account.account_id);
    // Optimistic flip so the switch feels instant; reconcile with the server row.
    updateAccount(account.account_id, { is_enabled: enabled });
    try {
      const updated = await api.toggleAccount(account.account_id, enabled);
      updateAccount(account.account_id, updated);
    } catch {
      // Roll back the optimistic change on failure.
      updateAccount(account.account_id, { is_enabled: !enabled });
      toastQueue.add({ title: t('dashboard.toggleFailed', { name: accountName(account) }) });
    } finally {
      setTogglingId(null);
    }
  };

  // Throttle state is the Dashboard's own and arrives a beat after the list.
  useEffect(() => {
    let alive = true;
    api.getThrottleStatus()
      .then((throttle) => { if (alive) setThrottledIds(new Set(throttle.throttled_account_ids)); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const getSubscriptionLabel = (status?: AccountSubscriptionStatus) => {
    if (isOnDemand(status)) return t(status?.billing_suspended ? 'accountView.onDemandPaused' : 'accountView.onDemand');
    if (!status?.active) return t('dashboard.noPlan', { defaultValue: 'No plan' });
    if (status.plan_code === 'monthly_core_4_features') {
      return t('dashboard.planAssistant', { defaultValue: 'Assistant' });
    }
    if (status.plan_code === 'monthly_core_no_assistant') {
      return t('dashboard.planNoAssistant', { defaultValue: 'No assistant' });
    }
    return status.plan_code || t('dashboard.planActive', { defaultValue: 'Active plan' });
  };

  const counts = useMemo(() => {
    const active = accounts.filter(isActive).length;
    return { all: accounts.length, active, inactive: accounts.length - active };
  }, [accounts]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return accounts.filter((a) => {
      if (tab === 'active' && !isActive(a)) return false;
      if (tab === 'inactive' && isActive(a)) return false;
      if (q) {
        const name = accountName(a).toLowerCase();
        const handle = (a.username || '').toLowerCase();
        if (!name.includes(q) && !handle.includes(q)) return false;
      }
      return true;
    });
  }, [accounts, tab, query]);

  // Subscription tracker — connected/with-plan counts, nearest renewal, health line.
  const tracker = useMemo(() => {
    const sub = (a: Account) => subscriptionMap[a.account_id];
    // With a plan = Assistant + No assistant (CRM dashboard rule). Without a plan
    // excludes accounts running On demand — they are billed, just not on a plan.
    const withPlan = accounts.filter((a) => hasPaidPlan(sub(a))).length;
    const noPlan = accounts.filter((a) => !hasPaidPlan(sub(a)) && !isActiveOnDemand(sub(a)));
    const expiringSoon = accounts
      .filter((a) => { const s = sub(a); return Boolean(s?.active && s.days_left != null && s.days_left < 5); })
      .sort((x, y) => (sub(x)!.days_left! - sub(y)!.days_left!));
    // Next renewal considers every active plan that has a finite end date.
    const renewal = accounts
      .filter((a) => { const s = sub(a); return Boolean(s?.active && s.days_left != null); })
      .sort((x, y) => (sub(x)!.days_left! - sub(y)!.days_left!))[0];

    let renewalValue: string;
    if (renewal) {
      const d = sub(renewal)!.days_left!;
      renewalValue = d <= 0
        ? t('dashboard.overview.nextRenewalToday', { name: accountName(renewal) })
        : t('dashboard.overview.nextRenewalValue', { name: accountName(renewal), days: d });
    } else {
      renewalValue = t('dashboard.overview.nextRenewalNone');
    }

    let statusTone: Tone;
    let statusText: string;
    const firstNoPlan = noPlan[0];
    const onlyExpiring = expiringSoon.length === 1 ? expiringSoon[0] : undefined;
    if (firstNoPlan) {
      statusTone = 'bad';
      statusText = noPlan.length === 1
        ? t('dashboard.overview.statusNoPlanOne', { name: accountName(firstNoPlan) })
        : t('dashboard.overview.statusNoPlanMany', { count: noPlan.length });
    } else if (onlyExpiring) {
      statusTone = 'warn';
      const d = sub(onlyExpiring)!.days_left!;
      statusText = d <= 0
        ? t('dashboard.overview.statusExpiringToday', { name: accountName(onlyExpiring) })
        : t('dashboard.overview.statusExpiring', { name: accountName(onlyExpiring), count: d });
    } else if (expiringSoon.length > 0) {
      statusTone = 'warn';
      statusText = t('dashboard.overview.statusExpiringMany', { count: expiringSoon.length });
    } else {
      statusTone = 'ok';
      statusText = t('dashboard.overview.statusHealthy');
    }

    return { withPlan, renewalValue, statusTone, statusText };
  }, [accounts, subscriptionMap, t]);

  if (loading) {
    return (
      <DashboardSkeleton />
    );
  }

  const tabs: { id: FilterTab; label: string; n: number }[] = [
    { id: 'all', label: t('dashboard.filterAll'), n: counts.all },
    { id: 'active', label: t('dashboard.filterActive'), n: counts.active },
    { id: 'inactive', label: t('dashboard.filterInactive'), n: counts.inactive },
  ];

  return (
    <PageTransition>
      <div className="mobile-page-shell px-6 pt-5 pb-6 max-w-[1600px] mx-auto space-y-8 min-w-0">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <PageHeading title={t('dashboard.title')} />
          {offerNew && (
            <Link to="/dashboard" className="text-sm font-medium text-[var(--om-accent-2)] hover:text-[var(--om-accent-3)]">
              {t('dashboard.tryNew')}
            </Link>
          )}
        </div>

        {/* Subscription tracker */}
        {accounts.length > 0 && (
          <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
            <StatTile icon="layers" tone="neutral" numeric value={counts.all} label={t('dashboard.overview.connected')} />
            <StatTile icon="zap" tone="ok" numeric value={tracker.withPlan} label={t('dashboard.overview.withPlan')} />
            <StatTile icon="calendar" tone="accent" value={tracker.renewalValue} label={t('dashboard.overview.nextRenewal')} />
            <StatTile icon="shield" tone={tracker.statusTone} value={tracker.statusText} label={t('dashboard.overview.status')} />
            {throttledIds.size > 0 && (
              <StatTile
                icon="alert-triangle"
                tone="warn"
                numeric
                value={throttledIds.size}
                label={t('dashboard.overview.throttled', 'Throttled')}
              />
            )}
          </div>
        )}

        {/* Your Accounts */}
        <section>
          <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
            <div className="flex items-center gap-4 flex-wrap">
              <h2 className="text-[15px] font-semibold text-[var(--color-text-primary)]">{t('dashboard.yourAccounts')}</h2>
              {accounts.length > 0 && (
                <div className="inline-flex items-center gap-1 rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-subtle)] p-1">
                  {tabs.map((o) => {
                    const on = o.id === tab;
                    return (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => setTab(o.id)}
                        className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${
                          on ? 'bg-[var(--color-accent-primary)] text-white' : 'text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)]'
                        }`}
                      >
                        {o.label}
                        <span className={`text-[11px] font-bold tabular-nums rounded-full px-1.5 py-px ${on ? 'bg-white/20 text-white' : 'bg-white/5 text-[var(--color-text-tertiary)]'}`}>
                          {o.n}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {accounts.length > 0 && (
                <>
                  <div className="flex items-center gap-2 rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-input)] px-3 py-2 w-[220px]">
                    <FeatherIcon name="search" size={15} className="text-[var(--color-text-tertiary)] shrink-0" />
                    <input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder={t('dashboard.search')}
                      className="bg-transparent outline-none border-none text-sm text-[var(--color-text-primary)] w-full placeholder:text-[var(--color-text-tertiary)]"
                    />
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {selectionMode && (
                      <>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          isDisabled={bulkToggling !== null}
                          onPress={() => setSelectedIds(new Set(accounts.map((a) => a.account_id)))}
                        >
                          {t('dashboard.bulkSelectAll')}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          isDisabled={bulkToggling !== null || selectedIds.size === 0}
                          onPress={() => setSelectedIds(new Set())}
                        >
                          {t('dashboard.bulkClear')}
                        </Button>
                        <span className="text-xs text-[var(--color-text-tertiary)]">
                          {t('dashboard.bulkSelectedCount', { count: selectedIds.size })}
                        </span>
                      </>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="border-green-500/40 text-green-400 hover:bg-green-500/10 hover:border-green-500/60"
                      isDisabled={bulkToggling !== null || (selectionMode && selectedIds.size === 0)}
                      onPress={() => handleBulkToggle(true)}
                    >
                      <FeatherIcon name="power" size={14} className="mr-1.5" />
                      {bulkToggling === 'on'
                        ? t('dashboard.bulkTurningOn')
                        : hasSelection
                          ? t('dashboard.bulkTurnOnSelected', { count: selectedIds.size })
                          : t('dashboard.bulkTurnAllOn')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="border-red-500/40 text-red-400 hover:bg-red-500/10 hover:border-red-500/60"
                      isDisabled={bulkToggling !== null || (selectionMode && selectedIds.size === 0)}
                      onPress={() => handleBulkToggle(false)}
                    >
                      <FeatherIcon name="power" size={14} className="mr-1.5" />
                      {bulkToggling === 'off'
                        ? t('dashboard.bulkTurningOff')
                        : hasSelection
                          ? t('dashboard.bulkTurnOffSelected', { count: selectedIds.size })
                          : t('dashboard.bulkTurnAllOff')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={selectionMode ? 'primary' : 'ghost'}
                      isDisabled={bulkToggling !== null}
                      onPress={() => (selectionMode ? exitSelection() : setSelectionMode(true))}
                    >
                      <FeatherIcon name={selectionMode ? 'x' : 'check-square'} size={14} className="mr-1.5" />
                      {selectionMode ? t('dashboard.bulkSelectDone') : t('dashboard.bulkSelect')}
                    </Button>
                  </div>
                </>
              )}
              <span className="text-sm text-[var(--color-text-tertiary)]">
                {t('dashboard.accountsCount', { count: accounts.length })}
              </span>
            </div>
          </div>

          {accounts.length === 0 ? (
            <div className="rounded-2xl bg-[var(--color-bg-card)] border border-[var(--color-border-default)] flex flex-col items-center py-12 text-[var(--color-text-tertiary)]">
              <FeatherIcon name="inbox" size={40} className="mb-3 opacity-50" />
              <p>{t('dashboard.noAccounts')}</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-[var(--color-text-tertiary)]">
              {t('dashboard.noMatch', { query })}
            </div>
          ) : (
            <>
              <div className="grid gap-3.5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {filtered.map((account) => {
                  const selected = selectedIds.has(account.account_id);
                  const subscription = subscriptionMap[account.account_id];
                  const hasSubscription = isPlanCovered(subscription);
                  const active = isActive(account);
                  const signedOut = account.is_enabled && !account.is_logged_in;
                  const statusLabel = active
                    ? t('dashboard.statusActive')
                    : signedOut
                      ? t('dashboard.statusSignedOut')
                      : t('dashboard.statusInactive');
                  const statusColor = active ? 'var(--stat-enabled)' : signedOut ? '#f5a524' : 'var(--om-dim)';
                  const throttled = throttledIds.has(account.account_id);
                  return (
                    <div
                      key={account.account_id}
                      onClick={() =>
                        selectionMode
                          ? toggleSelected(account.account_id)
                          : navigate(`/account/${account.account_id}`)
                      }
                      className={`group rounded-[15px] bg-[var(--color-bg-card)] border p-4 transition-all ${
                        selectionMode
                          ? 'cursor-pointer'
                          : 'cursor-pointer hover:-translate-y-0.5 hover:border-[var(--color-card-hover-border)]'
                      } ${
                        selected
                          ? 'border-[var(--color-accent-primary)] ring-1 ring-[var(--color-accent-primary)]'
                          : 'border-[var(--color-border-default)]'
                      }`}
                    >
                      {/* top row: identity + control */}
                      <div className="flex items-start gap-3">
                        {selectionMode && (
                          <span
                            className={`mt-0.5 shrink-0 w-5 h-5 rounded-md flex items-center justify-center border ${
                              selected
                                ? 'bg-[var(--color-accent-primary)] border-[var(--color-accent-primary)] text-white'
                                : 'border-[var(--color-text-tertiary)] text-transparent'
                            }`}
                          >
                            <FeatherIcon name="check" size={13} />
                          </span>
                        )}
                        <AccountAvatar avatarUrl={account.avatar_url} platform={account.platform} size={42} className="shrink-0" />
                        <div className="min-w-0 flex-1 pt-0.5">
                          <p className="font-bold text-[15px] text-[var(--color-text-primary)] truncate leading-tight">
                            {account.profile_name || account.account_id}
                          </p>
                          <p className="text-xs text-[var(--color-text-tertiary)] truncate font-mono mt-0.5">
                            {account.username ? `@${account.username}` : '—'}
                          </p>
                        </div>
                        {!selectionMode && (
                          <PowerToggle
                            on={account.is_enabled}
                            disabled={togglingId === account.account_id}
                            onChange={(next) => handleToggleOne(account, next)}
                          />
                        )}
                      </div>

                      <div className="h-px bg-[var(--color-border-default)] my-3" />

                      {/* bottom row: status + plan */}
                      <div className="flex items-center gap-2.5">
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: statusColor }}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusColor }} />
                          {statusLabel}
                        </span>
                        {throttled && (
                          // Distinct amber marker — NOT the status dot. A throttled
                          // account is still logged in (shows Active), but rate-limited
                          // into silence right now. See docs/adr/0009.
                          <span
                            className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-semibold leading-none"
                            style={{ color: '#f5a524', background: 'rgba(245,165,36,0.12)' }}
                            title={t('dashboard.throttledHint', 'Rate-limited into silence right now')}
                          >
                            <FeatherIcon name="alert-triangle" size={11} />
                            {t('dashboard.throttled', 'Throttled')}
                          </span>
                        )}
                        <span
                          className={`max-w-full rounded-md px-2 py-0.5 text-[11px] font-semibold leading-none border ${
                            hasSubscription
                              ? 'bg-[var(--stat-enabled-bg)] text-[var(--stat-enabled)] border-transparent'
                              : 'bg-[#f5a524]/10 text-[#f5a524] border-[#f5a524]/30'
                          }`}
                        >
                          {getSubscriptionLabel(subscription)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="mt-6 text-center text-sm text-[var(--color-text-tertiary)]">
                {t('dashboard.showingCount', { shown: filtered.length, total: accounts.length })}
              </p>
            </>
          )}
        </section>
      </div>
    </PageTransition>
  );
}
